import { getStrapiToken } from '@/services/auth/getStrapiToken';
// @/services/app/store/modules/customer-requests.js
import { clearChangeTracking } from '@/utils/changeDetection';
import { isSnowflakeFormat, fixSnowflakeTimezone, getEffectiveSubmitDate, calculateNeedsAttention as computeNeedsAttention } from '@/utils/dateUtils';

/**
 * CustomerRequestsHandler
 * 
 * Manages customer request data lifecycle for supplier-customer interactions with:
 * - Efficient loading and processing of request data with abort controllers
 * - Request status management with attention flags for UI notifications
 * - Date normalization for consistent timestamp comparison across formats
 * - Optimistic UI updates for immediate user feedback
 * - Self-assessment submission with coordinated store updates
 * - Cross-module coordination for business segments and company questions
 */
export class CustomerRequestsHandler {
  /**
   * Initialize the customer requests handler with a store reference
   * @param {Object} store - The main application store instance
   */
  constructor(store) {
    this.store = store;
    this.isLoading = false;
    this.globalLastSubmitDate = null;
    this.abortControllers = new Map();
    // Post-submission cooldown: prevents WebSocket-triggered reloads from
    // overwriting optimistic state before Snowflake has propagated
    this.submissionCooldownUntil = 0;
  }

  /**
   * Load customer requests from API with proper caching control
   * @param {boolean} silent - Whether to set loading state in the store
   * @returns {Promise<Array>} Processed and sorted customer requests
   */
  async loadCustomerRequests(silent = false) {
    // Skip if already loading to prevent duplicate requests
    if (this.isLoading) {
      // Already loading logging silenced
      return this.store.state.data.customerRequests?.requests || [];
    }

    // Skip WebSocket-triggered reloads during post-submission cooldown.
    // The optimistic state from _applySubmissionUpdates is authoritative —
    // populate-triggered reloads during this window would overwrite it with
    // stale Snowflake data (badges flash back then disappear).
    if (silent && Date.now() < this.submissionCooldownUntil) {
      console.log('[Customer Requests] Skipping reload during post-submission cooldown');
      return this.store.state.data.customerRequests?.requests || [];
    }

    // Create unique load ID and abort controller for cancellation
    const loadId = Date.now();
    const abortController = new AbortController();
    this.abortControllers.set(loadId, abortController);

    // Set loading state
    this.isLoading = true;
    if (!silent) {
      this.store.state.loading.customerRequests = true;
    }

    try {
      // Load last submission date first for proper needsAttention calculation
      await this.getLastSubmissionDate(true);

      // Get project ID from store
      const projectId = this.store.getProjectId();
      if (!projectId) {
        throw new Error('No project ID available');
      }

      // Loading from API logging silenced

      // Fetch requests from API
      const response = await fetch(
        `${process.env.GATSBY_STRAPI_URL}/api/customer-requests/project/${projectId}`,
        {
          headers: {
            Authorization: `Bearer ${getStrapiToken()}`
          },
          cache: 'no-cache',
          signal: abortController.signal
        }
      );

      // Handle unsuccessful responses
      if (!response.ok) {
        throw new Error(`Failed to fetch customer requests: ${response.status}`);
      }

      // Parse response data
      const { data } = await response.json();
      if (!Array.isArray(data)) {
        throw new Error('Invalid data format received');
      }

      // Process to add needsAttention flag and sort
      const processedRequests = this.processRequests(data);
      const sortedRequests = this.sortRequests(processedRequests);

      // Update store with single operation for better React rendering
      this.updateStoreWithRequests(sortedRequests);

      // Persist to sessionStorage so fast-path reload has correct needsAttention state.
      // This covers ALL WebSocket-driven reloads (reminders, invites, status changes, etc.)
      // Without this, reload after a real-time event briefly shows stale cached state.
      if (this.store.saveState) {
        try {
          await this.store.saveState();
        } catch (e) {
          // Non-critical — worst case is a brief flash on next reload
        }
      }

      return sortedRequests;
    } catch (error) {
      // Ignore abort errors (they're intentional cancellations)
      if (error.name !== 'AbortError') {
        console.error('Error loading customer requests:', error);
        this.store.handleError('customerRequests', error);
      }
      return this.store.state.data.customerRequests?.requests || [];
    } finally {
      // Clean up regardless of outcome
      this.abortControllers.delete(loadId);
      this.isLoading = false;
      if (!silent) {
        this.store.state.loading.customerRequests = false;
      }
    }
  }

  /**
   * Process requests to include needsAttention flag for UI notifications
   * @private
   * @param {Array} requests - Raw requests from API
   * @returns {Array} Processed requests with needsAttention flag
   */
  processRequests(requests) {
    if (!Array.isArray(requests)) return [];

    const globalFallback = this.globalLastSubmitDate ||
      this.store.state.data.customerRequests?.lastSubmitDate || null;

    return requests.map(request => ({
      ...request,
      needsAttention: computeNeedsAttention(request, globalFallback)
    }));
  }

  /**
   * Sort requests by status priority and date
   * @private
   * @param {Array} requests - Processed requests
   * @returns {Array} Sorted requests (invited > accepted > declined, then by most recent)
   */
  sortRequests(requests) {
    // Status priority: invited > accepted > declined
    const statusOrder = { invited: 0, accepted: 1, declined: 2 };

    return [...requests].sort((a, b) => {
      // First compare status
      const statusA = (a.Status || '').toLowerCase();
      const statusB = (b.Status || '').toLowerCase();
      const statusDiff = statusOrder[statusA] - statusOrder[statusB];
      if (statusDiff !== 0) return statusDiff;

      // Sort by the most recent reminder date
      const dateA = new Date(a.LastLinkingRequest || a.LinkingCreated || 0);
      const dateB = new Date(b.LastLinkingRequest || b.LinkingCreated || 0);

      // Newest dates at top
      const dateDiff = dateB - dateA;
      if (dateDiff !== 0) return dateDiff;

      // Add a stable tertiary sort key - LinkingId
      return (a.LinkingId || 0) - (b.LinkingId || 0);
    });
  }

  /**
   * Update store with processed requests using immutable pattern
   * @private
   * @param {Array} requests - Processed and sorted requests
   */
  updateStoreWithRequests(requests) {
    const openCount = requests.filter(r => r.needsAttention).length;

    // Create new object for store update with proper immutable references
    this.store.state.data.customerRequests = {
      requests,
      lastSubmitDate: this.globalLastSubmitDate,
      openCount,
      meta: {
        lastSync: new Date().toISOString(),
        count: requests.length
      }
    };

    // Notify subscribers of the update
    this.store.notifySubscribers('customerRequests');
  }

  /**
   * Get last submission date from API for needsAttention calculations
   * @param {boolean} silent - Whether to do this silently without UI indicators
   * @returns {Promise<string|null>} Last submission date or null
   */
  async getLastSubmissionDate() {
    try {
      const projectId = this.store.getProjectId();
      if (!projectId) return null;

      const response = await fetch(
        `${process.env.GATSBY_STRAPI_URL}/api/customer-requests/project/${projectId}/last-submission-date`,
        {
          headers: {
            Authorization: `Bearer ${getStrapiToken()}`
          }
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch last submission date: ${response.status}`);
      }

      const { lastSubmitDate } = await response.json();

      // Normalize the date if it's in Snowflake format
      let normalizedLastSubmitDate = lastSubmitDate;
      if (isSnowflakeFormat(lastSubmitDate)) {
        normalizedLastSubmitDate = fixSnowflakeTimezone(lastSubmitDate);
      }

      // Don't let API response regress the optimistic lastSubmitDate
      // (handles race condition where Snowflake hasn't propagated the new date yet)
      if (this.globalLastSubmitDate) {
        if (!normalizedLastSubmitDate || new Date(this.globalLastSubmitDate) > new Date(normalizedLastSubmitDate)) {
          normalizedLastSubmitDate = this.globalLastSubmitDate;
        }
      }

      // Update global variable with normalized date
      this.globalLastSubmitDate = normalizedLastSubmitDate;

      // Update store if different from current value
      if (this.store.state.data.customerRequests?.lastSubmitDate !== normalizedLastSubmitDate) {
        const currentData = this.store.state.data.customerRequests;
        if (currentData) {
          // Create new object with updated lastSubmitDate
          this.store.state.data.customerRequests = {
            ...currentData,
            lastSubmitDate: normalizedLastSubmitDate
          };

          // If we have requests data, recalculate needs attention
          if (currentData.requests?.length) {
            const updatedRequests = this.processRequests(currentData.requests);
            this.store.state.data.customerRequests.requests = updatedRequests;
            this.store.state.data.customerRequests.openCount =
              updatedRequests.filter(r => r.needsAttention).length;
          }

          // Notify subscribers
          this.store.notifySubscribers('customerRequests');
        }
      }

      return normalizedLastSubmitDate;
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to get last submission date:', error);
      }
      return null;
    }
  }

  /**
   * Update a single request without reloading everything
   * @param {Object} updatedRequest - The updated request data
   * @returns {boolean} Success status
   */
  updateSingleRequest(updatedRequest) {
    if (!updatedRequest?.LinkingId) {
      return false;
    }

    try {
      const currentData = this.store.state.data.customerRequests;
      if (!currentData?.requests) {
        return false;
      }

      const requests = currentData.requests;
      const index = requests.findIndex(req => req.LinkingId === updatedRequest.LinkingId);
      let updatedRequests;

      if (index === -1) {
        // New request - add if it has a status
        if (!updatedRequest.Status) return false;

        // Add with needsAttention calculated
        const processedRequest = {
          ...updatedRequest,
          needsAttention: this.calculateNeedsAttention(updatedRequest)
        };

        updatedRequests = [...requests, processedRequest];
      } else {
        // Update existing request with needsAttention calculated
        updatedRequests = requests.map(req => {
          if (req.LinkingId === updatedRequest.LinkingId) {
            const mergedRequest = { ...req, ...updatedRequest };
            return {
              ...mergedRequest,
              needsAttention: this.calculateNeedsAttention(mergedRequest)
            };
          }
          return req;
        });
      }

      // Sort consistently
      const sortedRequests = this.sortRequests(updatedRequests);

      // Calculate open count
      const openCount = sortedRequests.filter(r => r.needsAttention).length;

      // Update store with new object
      this.store.state.data.customerRequests = {
        ...currentData,
        requests: sortedRequests,
        openCount,
        meta: {
          ...currentData.meta,
          lastSync: new Date().toISOString(),
          count: sortedRequests.length
        }
      };

      // Notify subscribers
      this.store.notifySubscribers('customerRequests');

      return true;
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error updating single request:', error);
      }
      return false;
    }
  }

  /**
   * Calculate if a request needs attention based on status and dates
   * @private
   * @param {Object} request - Request object to evaluate
   * @returns {boolean} Whether the request needs attention
   */
  calculateNeedsAttention(request) {
    const globalFallback = this.globalLastSubmitDate ||
      this.store.state.data.customerRequests?.lastSubmitDate || null;
    return computeNeedsAttention(request, globalFallback);
  }

  /**
   * Respond to a customer request (accept or decline)
   * @param {number} linkingId - Request ID to respond to
   * @param {string} action - "ACCEPT" or "DECLINE"
   * @returns {Promise<Object>} Response from API
   * @throws {Error} If operation fails or parameters invalid
   */
  async respondToRequest(linkingId, action) {
    if (!linkingId || !action) {
      throw new Error('Linking ID and action are required');
    }

    if (!['ACCEPT', 'DECLINE'].includes(action.toUpperCase())) {
      throw new Error('Invalid action - must be ACCEPT or DECLINE');
    }

    const projectId = this.store.getProjectId();
    if (!projectId) {
      throw new Error('No project ID available');
    }

    // Create abort controller for timeout
    const abortController = new AbortController();
    const timeout = setTimeout(() => abortController.abort(), 30000); // 30s timeout

    try {
      // Call the API endpoint
      const response = await fetch(
        `${process.env.GATSBY_STRAPI_URL}/api/customer-requests/project/${projectId}/respond`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${getStrapiToken()}`
          },
          body: JSON.stringify({ linkingId, action }),
          signal: abortController.signal
        }
      );

      clearTimeout(timeout);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error?.message || `Failed to ${action.toLowerCase()} request`);
      }

      const result = await response.json();
      const timestamp = new Date().toISOString();

      // Update the specific request in the store
      this.updateSingleRequest({
        LinkingId: linkingId,
        Status: action.toUpperCase() === 'ACCEPT' ? 'accepted' : 'declined',
        LastLinkingStatusChange: timestamp
      });

      // Repopulate business segments after accepting a request
      // This ensures that any new sector/country combinations are properly populated
      if (action.toUpperCase() === 'ACCEPT' && this.store.dataPopulationHandler) {
        // Use the data population handler to ensure all business segments are up to date
        this.store.dataPopulationHandler.repopulateAfterAction(
          ['businessSegments'],
          true,
          { action: 'accept', source: 'customer-request' }
        ).catch(error => {
          if (process.env.NODE_ENV === 'development') {
            console.error('Error repopulating business segments after accept:', error);
          }
        });
      }

      return result;
    } catch (error) {
      clearTimeout(timeout);
      if (process.env.NODE_ENV === 'development') {
        console.error(`Failed to ${action.toLowerCase()} request:`, error);
      }

      // Rethrow to allow component to handle the error
      throw error;
    }
  }

  /**
   * Submit self-assessment data to customers (non-blocking)
   *
   * The backend returns 202 immediately and processes the submission in the background.
   * The frontend waits for a WebSocket `submission:status` event to confirm completion.
   * This prevents Heroku H12 (30s) timeouts from ever affecting the user.
   *
   * @returns {Promise<Object>} Result of submission with success flag
   * @throws {Error} If the initial API call fails or WebSocket reports failure
   */
  async submitSelfAssessment() {
    const projectId = this.store.getProjectId();
    if (!projectId) {
      throw new Error('Cannot submit data - no project ID available');
    }

    // Set loading state
    this.isLoading = true;
    this.store.state.loading.customerRequests = true;

    try {
      // Step 1: Register WebSocket handler BEFORE sending POST to avoid race condition.
      // The backend returns 202 and runs _performSubmission in the background.
      // If the submission completes fast, the submission:status event can arrive
      // before we register the handler — so register first, POST second.
      const wsPromise = this.waitForSubmissionStatus(projectId, 60000); // 60s max wait

      // Step 2: Fire the submit request — backend returns 202 immediately
      const response = await fetch(
        `${process.env.GATSBY_STRAPI_URL}/api/customer-requests/project/${projectId}/submit`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${getStrapiToken()}`
          }
        }
      );

      const result = await response.json();

      // Handle actual HTTP errors (not 202)
      if (!response.ok && response.status !== 202) {
        throw new Error(result.message || 'Failed to submit self-assessment');
      }

      // Step 3: Now await the WebSocket confirmation (handler already listening)
      const wsResult = await wsPromise;

      if (wsResult.status === 'failed') {
        throw new Error(wsResult.error || 'Submission failed during processing');
      }

      // Step 4: Submission completed — update store optimistically
      const submissionTimestamp = wsResult.timestamp || new Date().toISOString();
      this.globalLastSubmitDate = submissionTimestamp;
      // Block WebSocket-triggered reloads for 30s so optimistic state isn't overwritten
      // by populate chains firing before Snowflake has propagated the new dates
      this.submissionCooldownUntil = Date.now() + 30000;
      this._applySubmissionUpdates(submissionTimestamp);

      // Step 5: Refresh data from server (WebSocket handleSubmissionEvent also does this,
      // but we do it here too to ensure consistency before returning)
      clearChangeTracking();
      await this._refreshAfterSubmission();

      return { success: true, timestamp: submissionTimestamp };
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to submit self-assessment:', error);
      }

      this.store.handleError('customerRequests', error);
      throw error;
    } finally {
      this.isLoading = false;
      this.store.state.loading.customerRequests = false;
    }
  }

  /**
   * Wait for a WebSocket `submission:status` event for this project
   * Resolves when the backend reports completion or failure
   *
   * @param {string|number} projectId - The project to wait for
   * @param {number} timeout - Max wait time in ms (default 60 seconds)
   * @returns {Promise<Object>} { status: 'completed'|'failed', timestamp, error? }
   */
  waitForSubmissionStatus(projectId, timeout = 60000) {
    const projectIdInt = parseInt(projectId, 10);

    return new Promise((resolve, reject) => {
      const handlerId = `submission-status-${projectIdInt}-${Date.now()}`;
      let timeoutId;

      const cleanup = () => {
        clearTimeout(timeoutId);
        if (this.store.webSocketHandler) {
          this.store.webSocketHandler.unregisterHandler(handlerId);
        }
      };

      // Register a one-shot WebSocket handler for submission:status
      if (this.store.webSocketHandler?.connected) {
        this.store.webSocketHandler.registerHandler(
          handlerId,
          (event) => {
            if (event.type === 'submission:status' && parseInt(event.projectId, 10) === projectIdInt) {
              cleanup();
              // Extract status from event.data (backend wraps payload in { type, data, timestamp })
              const statusData = event.data || {};
              resolve({
                status: statusData.status || event.action || 'completed',
                timestamp: statusData.timestamp || event.timestamp,
                error: statusData.error
              });
            }
          },
          ['submission:status']
        );
      } else {
        // No WebSocket connection — fall back to a fixed wait
        // The submission will still complete in the backend
        cleanup();
        setTimeout(() => {
          resolve({
            status: 'completed',
            timestamp: new Date().toISOString(),
            noWebSocket: true
          });
        }, 15000); // Wait 15s as a reasonable estimate
        return;
      }

      // Timeout fallback — if we never get a WebSocket event
      timeoutId = setTimeout(() => {
        cleanup();
        // After timeout, assume it completed (the Snowflake procedure always finishes)
        // The WebSocket handleSubmissionEvent from submission:complete will still refresh data
        resolve({
          status: 'completed',
          timestamp: new Date().toISOString(),
          timedOut: true
        });
      }, timeout);
    });
  }

  /**
   * Apply optimistic store updates after successful submission
   * @private
   */
  _applySubmissionUpdates(submissionTimestamp) {
    // 1. Update customer requests
    const customerRequestsData = this.store.state.data.customerRequests;
    if (customerRequestsData) {
      const updatedRequests = (customerRequestsData.requests || []).map(request => {
        if (request.Status?.toLowerCase() === 'accepted') {
          return { ...request, LastSubmitDate: submissionTimestamp, needsAttention: false };
        }
        return request;
      });

      this.store.state.data.customerRequests = {
        ...customerRequestsData,
        requests: updatedRequests,
        lastSubmitDate: submissionTimestamp,
        openCount: updatedRequests.filter(r => r.needsAttention).length
      };
      this.store.notifySubscribers('customerRequests');
    }

    // 2. Update business segments
    const businessSegments = this.store.state.data.businessSegments;
    if (businessSegments?.bySector) {
      const updatedBySector = {};
      Object.keys(businessSegments.bySector).forEach(sector => {
        updatedBySector[sector] = businessSegments.bySector[sector].map(segment => {
          if (segment.assessmentStatus !== 'submitted') {
            return { ...segment, assessmentStatus: 'submitted', submittedAt: submissionTimestamp };
          }
          return segment;
        });
      });

      this.store.state.data.businessSegments = {
        ...businessSegments,
        bySector: updatedBySector,
        meta: { ...businessSegments.meta, lastUpdated: submissionTimestamp }
      };
      this.store.notifySubscribers('businessSegments');
    }

    // 3. Update company questions
    const companyQuestionsData = this.store.state.data.companyQuestions;
    if (companyQuestionsData?.data && companyQuestionsData.data.assessmentStatus !== 'submitted') {
      this.store.state.data.companyQuestions = {
        ...companyQuestionsData,
        data: { ...companyQuestionsData.data, assessmentStatus: 'submitted' }
      };
      this.store.notifySubscribers('companyQuestions');
    }

    // Clear session storage flags
    if (typeof sessionStorage !== 'undefined') {
      try {
        sessionStorage.removeItem('lastUserChangeTimestamp');
        sessionStorage.removeItem('hasDraftChanges');
        sessionStorage.removeItem('lastDraftTimestamp');
      } catch (e) { /* non-critical */ }
    }
  }

  /**
   * Refresh all data from server after submission
   * @private
   */
  async _refreshAfterSubmission() {
    try {
      await this.getLastSubmissionDate(true);
      if (this.store.businessSegmentsHandler) {
        await this.store.businessSegmentsHandler.loadBusinessSegments(false);
        this.store.notifySubscribers('businessSegments');
      }
      if (this.store.companyQuestionsHandler) {
        await this.store.companyQuestionsHandler.loadCompanyQuestions(false);
        this.store.notifySubscribers('companyQuestions');
      }
      this.store.notifySubscribers('customerRequests');

      // Persist updated state to sessionStorage so fast-path reload
      // has correct lastSubmitDate/needsAttention — prevents badge flash on reload
      if (this.store.saveState) {
        await this.store.saveState();
      }
    } catch (error) {
      // Non-critical — WebSocket handleSubmissionEvent will also refresh
    }
  }

  /**
   * Clean up any pending requests or operations
   * Essential for proper component unmounting
   */
  cleanup() {
    // Cancel all pending requests
    this.abortControllers.forEach(controller => {
      try {
        controller.abort();
      } catch (e) {
        // Ignore errors during cleanup
      }
    });
    this.abortControllers.clear();

    // Reset state
    this.isLoading = false;
  }
}